import { createSelector } from "@reduxjs/toolkit";
import { AdaptSliceState, localAdaptSelectors } from "../../adapt/adaptSlice";
import { ContouringSliceState, localContouringSelectors } from "../../contouring/contouringSlice";
import { DoseSliceState, localDoseSelectors } from "../../dose/doseSlice";
import { ImageSliceState, localImageSelectors } from "../../image/imageSlice";
import { Modality, Model, ModelVisibility } from "../customization-types";
import { AnySliceState } from "../reducer-helpers";
import { naturalSort } from "../../../util/sort";
import { getAllTagsInOrder, getAllVersionTagsInOrder } from "../customizationSelectors";


// some selectors are required for all slices so we define
// them here to help typescript
type RequiredSelectors<T extends AnySliceState> = {
    selectModels: (state: T) => Model[],
    selectActiveTags: (state: T) => string[],
    selectActiveInputModalities: (state: T) => Modality[],
    selectActiveOutputModalities: (state: T) => Modality[],
    selectActiveVersionTags: (state: T) => string[],
}

type LocalSelectorsType<T> = T extends ContouringSliceState
    ? typeof localContouringSelectors
    : T extends DoseSliceState
    ? typeof localDoseSelectors
    : T extends ImageSliceState
    ? typeof localImageSelectors
    : T extends AdaptSliceState
    ? typeof localAdaptSelectors
    : never;

/**
 * An abstract class that provides a set of generic selectors which are needed for every
 * model type and their redux slices. This class is designed to work with a state slice and its associated
 * internal selectors, and should be used by creating an instance of this in a model type's
 * separate selectors file.
 *
 * @template T - Represents the state type of the slice, extending `AnySliceState`.
 * @template S - Represents the required selectors type, extending `RequiredSelectors<T>` and `LocalSelectorsType<T>`.
 *
 * @property {S} selectors - The set of selectors used to retrieve data from the state slice.
 * 
 */
export default abstract class GenericSelectors<T extends AnySliceState, S extends RequiredSelectors<T> & LocalSelectorsType<T>> {
    selectors: S;

    constructor(
        selectors: S,
    ) {
        this.selectors = selectors;
    }

    /** Returns all models that currently pass filtering using tags, modalities, and version tags. */
    public selectActiveFilteredModels = createSelector([
        (state: T) => this.selectors.selectModels(state),
        (state: T) => this.selectors.selectActiveTags(state),
        (state: T) => this.selectors.selectActiveInputModalities(state),
        (state: T) => this.selectors.selectActiveOutputModalities(state),
        (state: T) => this.selectors.selectActiveVersionTags(state),
    ], (models, activeTagFilters, activeInputModalityFilters, activeOutputModalityFilters, activeVersionTags: string[]) => {
        if (activeTagFilters.length === 0 && activeInputModalityFilters.length === 0 && activeOutputModalityFilters.length === 0 && activeVersionTags.length === 0) {
            return models; // no filters, return all models
        }

        // filter models by tag filters using AND logic (model must have all selected tags)
        const matchingModels: Model[] = [];
        for (const model of models) {
            if (activeTagFilters.length > 0 && !activeTagFilters.every(tag => model.tags.includes(tag))) {
                continue;
            }
            if (activeInputModalityFilters.length > 0 && !activeInputModalityFilters.every(modality => model.inputModalities.includes(modality))) {
                continue;
            }
            if (activeOutputModalityFilters.length > 0 && !activeOutputModalityFilters.every(modality => model.outputModalities.includes(modality))) {
                continue;
            }
            // Version tag filter: OR logic across selected version tags
            // - 'latest' matches models with isLatest === true
            // - other tags are matched by substring against version string
            if (activeVersionTags.length > 0) {
                const matchesAnyVersion = activeVersionTags.some(tag => tag === 'latest' ? model.isLatest === true : (model.version || '').includes(tag));
                if (!matchesAnyVersion) { continue; }
            }

            matchingModels.push(model);
        }
        return matchingModels;
    });

    /** Returns all contouring models that have their visibility set as Always Hidden. */
    public selectUserHiddenModels = createSelector(
        (state: T): Model[] => this.selectActiveFilteredModels(state),
        models => models.filter(m => m.visibility === ModelVisibility.AlwaysHidden));

    /** Returns all contouring models that are not in current user's license. */
    public selectNonLicensedModels = createSelector(
        (state: T): Model[] => this.selectActiveFilteredModels(state),
        models => models.filter(m => !m.isAvailable));

    /** Returns all contouring models that are deprecated. */
    public selectDeprecatedModels = createSelector(
        (state: T): Model[] => this.selectActiveFilteredModels(state),
        models => models.filter(m => m.isDeprecated));

    /** Returns all models that are latest. */
    public selectLatestModels = createSelector(
        (state: T): Model[] => this.selectActiveFilteredModels(state),
        models => models.filter(m => m.isLatest));

    /** Returns all models in alphabetical order. */
    public selectModelsInAlphabeticalOrder = createSelector(
        [(state: T): Model[] => this.selectActiveFilteredModels(state)],
        models => naturalSort(models, 'modelName'));

    /** Returns all version tags with 'latest' first, then version numbers in descending order. */
    public selectAllVersionTagsInOrder = createSelector(
        (state: T) => this.selectors.selectModels(state),
        models => getAllVersionTagsInOrder(models));

    /**
    * Returns all tags from all models in a sorted order ('latest' > versio numbers in descending order > other tags in alphabetical order).
    */
    public selectAllTagsInOrder = createSelector([
        (state: T) => this.selectors.selectModels(state),
    ], (models) => getAllTagsInOrder(models));

    public selectInputModalities = createSelector([
        (state: T) => this.selectors.selectModels(state),
    ], (models) => {
        const modalitiesSet = new Set<Modality>();
        models.forEach(model => {
            model.inputModalities.forEach(modality => modalitiesSet.add(modality));
        });
        return Array.from(modalitiesSet).sort();
    });

    public selectOutputModalities = createSelector([
        (state: T) => this.selectors.selectModels(state),
    ], (models) => {
        const modalitiesSet = new Set<Modality>();
        models.forEach(model => {
            model.outputModalities.forEach(modality => modalitiesSet.add(modality));
        });
        return Array.from(modalitiesSet).sort();
    });
}
